<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" name="button" data-test="btnCloseOffcanvas" @click="isNewPatient = false" :aria-label="$t('close')" v-if="isNewPatient">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <button class="btn-back" id="btnCloseAddPatient" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" @click="closeAddPatient" :aria-label="$t('close')" v-else>
        <IconArrowBack/>
        {{ reassign && currentPatientFullName ? currentPatientFullName : $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">
        {{ reassign ? $t('assignPatient.reassignPatient') : $t('assignPatient.assignPatient') }}
      </h5>
    </div>

    <ExamPatientAddFormData v-if="isNewPatient" @patient-created="doClose"/>

    <div class="offcanvas-body" v-else>
      <p>{{ reassign ? $t('assignPatient.reassignSearchPatient') : $t('assignPatient.searchPatient') }}</p>

      <div class="current-patient-box" v-if="reassign">
        <p>{{ $t('assignPatient.currentPatient') }}</p>
        <p>{{ currentPatientFullName }}</p>
      </div>

      <div class="form-floating">
        <input class="form-control" type="text" id="searchPatient" :placeholder="$t('patientSearch.patientSearchPlaceholder')" v-model="search" @keypress.enter="doSearch"/>
        <label for="searchPatient">{{ $t('patientSearch.patientSearchPlaceholder') }}</label>
        <button class="btn-icon" type="button" name="button" data-test="btnSearchPatient" @click="search ? clearSearch() : fetchData()">
          <IconClose v-if="search"/>
          <IconSearchFilter v-else/>
        </button>
      </div>

      <ul class="scrollbar">
        <li v-for="item in patientList" :key="item.uuid" :class="{'active' : item.uuid == selectedPatient.uuid}" @click="selectedPatient = item">
          <span>{{ item.fullName }}</span>

          <div class="id-box">
            <div class="icon-sub"></div>
            <span>{{ $utils.getFormattedPatientDocumentWithSearchMatch(item, searchValue, $i18n) }}</span>
          </div>
        </li>
      </ul>

      <p>{{ $t('assignPatient.createPatientInfo') }}</p>

      <!-- footer inside body to avoid empty space between body and footer -->
      <div class="offcanvas-footer">
        <button class="btn btn-outline-secondary" type="button" data-test="btnAdd" @click="isNewPatient = true">{{ $t('assignPatient.createPatient') }}</button>
        <button class="btn btn-success" type="button" name="button" :disabled="!selectedPatient.uuid" @click="doAssign">{{ reassign ? $t('assignPatient.linkAction') : $t('assignPatient.assign') }}</button>
      </div>
    </div>

    <teleport to="body">
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasReassignConfirm'" ref="offcanvasReassignConfirm"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
#ExamAssignPatientOffcanvas {
  z-index:                              1048;
  padding:                              0;

  .offcanvas-header {
    padding:                            40px 40px 0;
  }
}

.offcanvas {
  .offcanvas-body {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;
    padding:                            0 40px 40px 40px;

    p:not(.patient-selected) {
      color:                            var(--Brand-Primary);
      font-size:                        var(--font-size-14);
      font-weight:                      300;
      line-height:                      140%; /* 19.6px */
      margin-bottom:                    0;
    }

    .patient-selected {
      @include                          border_radius(99px);
      color:                            var(--Brand-Primary);
      text-align:                       center;
      font-size:                        18px;
      font-weight:                      700;
      line-height:                      normal;
      letter-spacing:                   0.9px;
      display:                          flex;
      justify-content:                  center;
      align-items:                      center;
      height:                           55px;
      margin-bottom:                    0;
      padding:                          20px;
      border:                           2px solid var(--Brand-Primary);
      background:                       var(--Shades-10);
    }

    .btn-icon {
      padding:                          0;
      border:                           0;

      svg {
        * {
          stroke:                       var(--Shades-00);
        }
      }
    }

    ul {
      display:                          block;
      max-height:                       calc(100vh - 500px);
      margin-bottom:                    0;
      padding:                          0 10px 0 0;

      li {
        @include                        border_radius(10px);
        color:                          var(--Brand-Primary);
        font-size:                      var(--font-size-14);
        font-weight:                    400;
        line-height:                    normal;
        letter-spacing:                 0.7px;
        text-overflow:                  ellipsis;
        display:                        flex;
        flex-wrap:                      wrap;
        overflow:                       hidden;
        margin-bottom:                  10px;
        padding:                        10px;
        background:                     var(--Shades-10);
        border:                         1px solid var(--Shades-20);
        cursor:                         pointer;

        .id-box {
          display:                      flex;
          gap:                          5px;
          width:                        100%;
          margin:                       5px 0 0 5px;

          .icon-sub {
            @include                    border_radius(1px);
            width:                      8px;
            height:                     8px;
            border-left:                1.5px solid var(--Shades-30);
            border-bottom:              1.5px solid var(--Shades-30);
          }

          span {
            color:                      var(--Shades-70);
            font-size:                  var(--font-size-12);
            font-weight:                400;
            line-height:                normal;
            letter-spacing:             0.6px;
          }
        }

        &.active {
          color:                        #28989F;
          background:                   #E0F0EF;
          border-color:                 #28989F;
        }

        &:last-child {
          margin:                       0;
        }
      }
    }

    .current-patient-box {
      display:                          flex;
      flex-direction:                   row;
      align-items:                      center;
      justify-content:                  flex-start;
      gap:                              16px;

      p {
        margin:                         0;
      }

      p:last-child:not(:first-child) {
        @include                        border_radius(99px);
        color:                          var(--Shades-90) !important;
        font-size:                      var(--font-size-15);
        font-weight:                    500;
        letter-spacing:                 0.7px;
        text-transform:                 capitalize;
        text-overflow:                  ellipsis;
        overflow:                       hidden;
        margin:                         0;
        padding:                        8.5px 10px;
        background:                     var(--Shades-10);
        border:                         1px solid var(--Shades-30);
      }
    }
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;

    .btn {
      color:                            var(--Brand-Primary);
      font-size:                        var(--font-size-14);
      font-weight:                      700;
      line-height:                      normal;
      letter-spacing:                   4.2px;
      text-align:                       center;
      text-transform:                   uppercase;
      width:                            100%;
      margin:                           0;
      padding:                          20px;

      &.btn-success {
        color:                          var(--Shades-W);
      }
    }
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .offcanvas {
    .offcanvas-body {
      justify-content:                  flex-start;
    }
  }
}
</style>

<script>
import ExamPatientAddFormData from './ExamPatientAddFormData.vue'

export default {
  name: 'ExamPatientAssignOffcanvas',
  props: {
    offcanvas_id: { type: String, default: null },
  },
  components: {
    ExamPatientAddFormData
  },
  data: () => ({
    isNewPatient: false,
    exam: {},
    patientList: {},
    search: null,
    selectedPatient: {},
    reassign: false,
    currentPatientFullName: null,
    offcanvas_assign: null,
    resolvePromise: null,
    rejectPromise: null
  }),
  mounted() {
    // this.fetchData()

    document.getElementById('searchPatient').focus();
  },
  methods: {
    show(options = {}) {
      this.selectedPatient = {}
      this.search = null
      this.reassign = false
      this.currentPatientFullName = null

      this.exam = options.exam

      if (options.reassign)
        this.reassign = options.reassign

      if (options.patientFullName)
        this.currentPatientFullName = options.patientFullName

      this.fetchData()

      this.offcanvas_assign = new bootstrap.Offcanvas(document.getElementById(this.offcanvas_id)).show();

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    doConfirm() {
      this.offcanvas_assign = null;
      this.resolvePromise(true)
    },

    doCancel() {
      this.offcanvas_assign = null;
      this.resolvePromise(false)
    },

    doClose(patient) {
      this.isNewPatient = false
      this.search = patient.fullName

      if (this.reassign && this.exam?.patient?.fullName)
        this.currentPatientFullName = this.exam.patient.fullName

      this.fetchData()
    },

    clearSearch() {
      this.search = null
      this.fetchData()
    },

    async fetchData(pageValue) {
      this.alert = {}

      let params = {
        page: 1
      }

      if (this.search)
        params.q = this.search

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$patientService.getList(params)
        this.patientList = response.result

        if (response.result.length === 0) {
          this.alert = this.$message.alert(this, 'request_notfound', 'warning')
          // this.$message.popup(this, 'request_notfound', 'operationWarning', 'warn')
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
      NProgress.done(true)
      this.loading = false
    },
    async doSearch() {
      this.alert = {}

      let params = {
        q: this.search
      }

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$patientService.getList(params)
        this.patientList = response.result

        if (response.result.length === 0) {
          // this.alert = this.$message.alert(this, 'request_notfound', 'warning')
          this.$message.popup(this, 'request_notfound', 'operationWarning', 'warn')
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    async doAssign() {
      if (!this.selectedPatient.uuid)
        return

      if (this.reassign) {
        const confirmed = await this.$refs.offcanvasReassignConfirm.show({
          title: this.$i18n.t('assignPatient.reassignConfirmTitle'),
          text: this.$i18n.t('assignPatient.reassignConfirmText'),
          confirmValue: this.selectedPatient.fullName,
          inputPlaceholder: this.$i18n.t('assignPatient.reassignConfirmPlaceholder'),
          okButton: this.$i18n.t('assignPatient.linkAction'),
          okButtonClass: 'btn-success'
        })

        if (!confirmed)
          return
      }

      let params = {
        exam: this.exam,
        patientUuid: this.selectedPatient.uuid
      }

      this.loading = true
      NProgress.start()
      try {
        let response = this.reassign ? await this.$examService.reassignPatient(params) :
                                       await this.$examService.updateAnonymous(params)

        if (!response.exam) {
          // this.alert = this.$message.alert(this, 'request_notfound', 'warning')
          this.$message.popup(this, 'request_notfound', 'operationWarning', 'warn')
        }
        this.$message.popup(this, this.reassign ? 'general.updatedSuccess' : 'general.createdSuccess', 'operationSuccess', 'success')

        document.getElementById('btnCloseAddPatient').click()
        this.$emit('patient-assigned', response.exam)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    closeAddPatient(){
       this.isNewPatient = false
    }
  }
}
</script>
